import type { PhotoCandid } from '@/api/types/photo'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { SIDES_ELEMENT_KIND_ID, isSidesEligibleShapeType } from '@/lib/object-effects/sides'
import { mapCandidToImageRefs } from './helpers/map-candid-to-image-refs'
import { resolveCanvasPhotoUrl } from '@/lib/photos/resolve-canvas-photo-url'

/** kindId values that support photo fill via SVG clipPath rendering. */
export const PHOTO_FILL_SUPPORTED_KIND_IDS = new Set([1, 2, 10])

/**
 * Resolves the effective kindId for a shape. Freshly drawn shapes (created via addShape)
 * have `type` but no `kindId` yet — infer from type using the same mapping as resolveKindId
 * in stowed-element-builder.ts so hit-testing works before the first API round-trip.
 */
function effectiveKindId(shape: ShapeData): number {
  if (typeof shape.kindId === 'number' && Number.isFinite(shape.kindId)) return shape.kindId
  if (shape.type === 'path') return 9
  if (shape.type === 'square') return 1
  if (shape.type && isSidesEligibleShapeType(shape.type as ShapeType)) return SIDES_ELEMENT_KIND_ID
  return 2
}

/**
 * Pen paths support fill only when closed (path ends with Z).
 * Identified by kindId 9 (API-loaded) OR type 'path' (newly drawn, kindId not yet assigned).
 */
function isClosedPenPath(shape: ShapeData): boolean {
  const isPenShape = shape.kindId === 9 || shape.type === 'path'

  if (!isPenShape) return false

  const path = typeof shape.path === 'string' ? shape.path.trim() : ''

  return /Z\s*$/i.test(path)
}

/**
 * Returns true when dragging a photo over this shape should trigger
 * a fill (kindId 1/2/9/10) rather than creating a new shape.
 * For kindId 9 (pen paths), only closed paths qualify.
 */
export function isPhotoDropTarget(shape: ShapeData): boolean {
  const k = effectiveKindId(shape)

  return PHOTO_FILL_SUPPORTED_KIND_IDS.has(k) || isClosedPenPath(shape)
}

/**
 * Returns true when the shape is a vector shape that supports photo fill
 * via SVG clipPath (kindId 1, 2, 10, or closed kindId 9).
 */
export function supportsPhotoFill(shape: ShapeData): boolean {
  return PHOTO_FILL_SUPPORTED_KIND_IDS.has(effectiveKindId(shape)) || isClosedPenPath(shape)
}

/** Build a ShapePhotoFill from an album photo. */
export function buildShapePhotoFill(
  photo: PhotoCandid,
  naturalSize?: { width: number; height: number }
): ShapePhotoFill {
  const refs = mapCandidToImageRefs(photo)

  return {
    imageId: refs.imageId,
    imageType: refs.imageType,
    pezId: refs.pezId,
    previewUrl: resolveCanvasPhotoUrl(photo),
    displayName: photo.displayName ?? '',
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    ...(naturalSize ? { naturalWidth: naturalSize.width, naturalHeight: naturalSize.height } : {}),
  }
}

/**
 * Resolves the placed-photo identity (candid id + display name) for UI and session-tracking
 * purposes — covers both a photo dropped into a hand-drawn shape and a photo dropped directly
 * onto the canvas (a real borderless rectangle + `photoFill` — see build-placed-photo-shape.ts).
 * Returns `null` when the shape isn't a placed photo.
 */
export function resolvePlacedPhotoIdentity(
  shape: ShapeData
): { imageId: string; displayName: string } | null {
  if (shape.photoFill?.imageId) {
    return { imageId: shape.photoFill.imageId, displayName: shape.photoFill.displayName || '' }
  }

  return null
}

/**
 * True when the placed fill is a Portraits-tab photo (`IV="Portrait"` / `imageType: 'Portrait'`).
 * Album candids stay `'Candid'` even when the same person also has a portrait record.
 */
export function isPortraitPlacedPhoto(shape: ShapeData): boolean {
  const imageType = shape.photoFill?.imageType ?? shape.imageType

  return typeof imageType === 'string' && imageType.toLowerCase() === 'portrait'
}
